import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { AlertTriangle, CreditCard, Calendar, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PaymentBlockedModal = ({ isOpen, onClose, tenantSlug, message }) => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [tenantId, setTenantId] = useState(null);

  useEffect(() => {
    if (isOpen) {
      loadPlans();
      loadTenantId();
    }
  }, [isOpen, tenantSlug]);

  const loadPlans = async () => {
    try {
      const response = await fetch(`${API_URL}/api/payments/plans`);
      const data = await response.json();
      setPlans(data.plans || []);
    } catch (error) {
      console.error('Erro ao carregar planos:', error);
    }
  };

  const loadTenantId = async () => {
    try {
      const response = await fetch(`${API_URL}/api/subscription/check/${tenantSlug}`);
      const data = await response.json();
      setTenantId(data.tenant_id);
    } catch (error) {
      console.error('Erro ao carregar tenant:', error);
    }
  };

  const handlePayment = async (planId) => {
    if (!tenantId) {
      alert('Erro: Tenant não identificado');
      return;
    }

    setLoading(true);
    setSelectedPlan(planId);

    try {
      const response = await fetch(`${API_URL}/api/payments/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          tenant_id: tenantId,
          plan: planId,
          origin_url: window.location.origin,
        }),
      });

      const data = await response.json();

      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        alert('Erro ao criar sessão de pagamento');
      }
    } catch (error) {
      console.error('Erro ao iniciar pagamento:', error);
      alert('Erro ao iniciar pagamento. Tente novamente.');
    } finally {
      setLoading(false);
      setSelectedPlan(null);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={() => {}}>
      <DialogContent className="sm:max-w-md" onPointerDownOutside={(e) => e.preventDefault()}>
        <DialogHeader className="text-center">
          <div className="mx-auto w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mb-4">
            <AlertTriangle className="w-6 h-6 text-red-600" />
          </div>
          <DialogTitle className="text-xl text-center">Acesso Bloqueado</DialogTitle>
          <DialogDescription className="text-center">
            {message || 'Sua assinatura está vencida. Regularize para continuar usando o sistema.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 mt-4">
          {plans.map((plan) => (
            <Button
              key={plan.id}
              variant={plan.id === 'yearly' ? 'default' : 'outline'}
              className="w-full h-auto py-4 flex flex-col items-start gap-1"
              onClick={() => handlePayment(plan.id)}
              disabled={loading}
              data-testid={`pay-${plan.id}-btn`}
            >
              <div className="flex items-center justify-between w-full">
                <div className="flex items-center gap-2">
                  {loading && selectedPlan === plan.id ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : plan.id === 'monthly' ? (
                    <CreditCard className="w-4 h-4" />
                  ) : (
                    <Calendar className="w-4 h-4" />
                  )}
                  <span className="font-semibold">{plan.name}</span>
                </div>
                <span className="font-bold">{plan.price_formatted}</span>
              </div>
              <div className="flex items-center justify-between w-full">
                <span className="text-xs text-muted-foreground">{plan.description}</span>
                {plan.id === 'yearly' && (
                  <Badge variant="secondary" className="text-xs">Economia 20%</Badge>
                )}
              </div>
            </Button>
          ))}
        </div>

        <div className="mt-4 pt-4 border-t text-center">
          <p className="text-xs text-muted-foreground">
            Pagamento seguro via Stripe. Aceitamos cartão de crédito.
          </p>
          <p className="text-xs text-muted-foreground mt-2">
            Dúvidas? Entre em contato: <a href="mailto:suporte@agrolinkbr.com.br" className="text-primary hover:underline">suporte@agrolinkbr.com.br</a>
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PaymentBlockedModal;
